import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import OcRequestStatusBadge from '../../components/OrganoidRequest/OcRequestStatusBadge';
import OcTechnicalReviewForm from '../../components/OrganoidRequest/OcTechnicalReviewForm';
import OcTransitionBar from '../../components/OrganoidRequest/OcTransitionBar';
import OcStageNav from '../../components/OrganoidRequest/OcStageNav';
import OcHandoffNotes from '../../components/OrganoidRequest/OcHandoffNotes';
import OcSelectionRequirementsPanel from '../../components/OrganoidRequest/OcSelectionRequirementsPanel';
import OcWorkspaceShell from '../../components/OrganoidRequest/OcWorkspaceShell';
import {
  getCompleteTechnicalReviewToast,
  getPrimaryTransitionForPage,
  getWorkflowHandoffNotes,
} from '../../utils/ocRequest';
import { useOcRequestWorkspace } from '../../utils/useOcRequestWorkspace';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo, logError } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'OcRequestTechnicalReviewPage';

/**
 * Technical review stage: edit all candidate forms, then one Submit at the bottom.
 * Outcomes: AWAITING_CLIENT_CONFIRMATION, CLIENT_APPROVED (skip client email),
 * or TECHNICAL_REVIEW_FAILED (admin; no manager step).
 */
const OcRequestTechnicalReviewPage = () => {
  const { api, requestId, basePath, mode, readOnly } = useOcRequestWorkspace();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [expandedCandidateId, setExpandedCandidateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const formRefs = useRef({});

  const load = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    const [detailRes, candRes] = await Promise.all([
      api.getDetail(),
      api.getCandidates(),
    ]);
    setLoading(false);
    if (!detailRes.success) {
      showToast(detailRes.messages?.[0] || 'Load failed', 'error', 'top-right', 4000);
      return;
    }
    setRequest(detailRes.data);
    setCandidates(candRes.success ? candRes.data || [] : []);
    logInfo(LOG_CONTEXT, 'technical review page loaded', {
      requestId,
      mode,
      candidateCount: candRes.success ? (candRes.data || []).length : null,
      handoffNoteCount: getWorkflowHandoffNotes(detailRes.data?.workflow_actions)
        .length,
    });
  }, [api, requestId, mode]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Validate + POST every candidate review, then let the bar call complete_technical_review.
   */
  const beforeSubmit = async action => {
    if (action !== 'complete_technical_review') return true;
    if (!candidates.length) {
      showToast('No candidates to review', 'warning', 'top-right', 3000);
      return false;
    }

    const reviews = [];
    for (const c of candidates) {
      const formApi = formRefs.current[c.id];
      if (!formApi) {
        showToast('Review forms still loading', 'warning', 'top-right', 3000);
        return false;
      }
      const check = formApi.validate();
      if (!check.ok) {
        showToast(check.message, 'error', 'top-right', 4000);
        return false;
      }
      reviews.push(formApi.getPayload());
    }

    logInfo(LOG_CONTEXT, 'persisting technical reviews before complete', {
      requestId,
      count: reviews.length,
    });

    for (const review of reviews) {
      const { candidate_id: candidateId, ...body } = review;
      const result = await api.saveTechnicalReview(candidateId, body);
      if (!result.success) {
        logError(LOG_CONTEXT, 'saveTechnicalReview failed', {
          candidateId,
          messages: result.messages,
        });
        showToast(
          result.messages?.[0] || `Failed to save review for candidate ${candidateId}`,
          'error',
          'top-right',
          5000
        );
        return false;
      }
    }

    return {
      extraPayload: {
        // Backend may upsert from this list and/or rely on POSTed rows.
        technical_reviews: reviews,
      },
    };
  };

  if (loading || !request || !api) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const primary = getPrimaryTransitionForPage('technical-review', request.status);
  const body = (
    <>
      <Button as={Link} to={basePath} variant="link" className="px-0 mb-2">
        ← Back to detail
      </Button>
      <div className="d-flex align-items-center gap-2 mb-3">
        <h2 className="mb-0">Technical review — {request.request_number}</h2>
        <OcRequestStatusBadge status={request.status} />
      </div>
      <OcStageNav basePath={basePath} active="technical-review" />

      <OcSelectionRequirementsPanel request={request} />

      <p className="text-muted small mb-3">
        Open one OC card at a time to review it. Choose a decision — only the fields
        for that decision appear. Submit once at the bottom.
      </p>

      <OcHandoffNotes actions={request.workflow_actions} />

      {!candidates.length ? (
        <p className="text-muted">No candidates to review.</p>
      ) : (
        candidates.map(c => (
          <OcTechnicalReviewForm
            key={c.id}
            ref={el => {
              if (el) formRefs.current[c.id] = el;
              else delete formRefs.current[c.id];
            }}
            api={api}
            candidate={c}
            readOnly={readOnly}
            expanded={expandedCandidateId === c.id}
            onToggle={id => {
              setExpandedCandidateId(prev => (prev === id ? null : id));
              logInfo(LOG_CONTEXT, 'tech review card toggled', {
                requestId,
                candidateId: id,
                expanded: expandedCandidateId !== id,
              });
            }}
          />
        ))
      )}

      {!readOnly ? (
      <OcTransitionBar
        api={api}
        requestId={requestId}
        request={request}
        action={primary?.action || 'complete_technical_review'}
        label={primary?.label || 'Submit technical review'}
        beforeTransition={beforeSubmit}
        onSuccess={updated => {
          setRequest(updated);
          logInfo(LOG_CONTEXT, 'complete_technical_review result', {
            requestId,
            status: updated?.status,
          });
          const toast = getCompleteTechnicalReviewToast(updated?.status);
          if (!toast) return;
          showToast(
            toast.message,
            toast.variant,
            'top-right',
            toast.variant === 'warning' ? 5000 : 4000
          );
          navigate(basePath);
        }}
      />
      ) : null}
    </>
  );

  if (mode === 'labPublic') return body;
  return <OcWorkspaceShell wide theme="yellow">{body}</OcWorkspaceShell>;
};

export default OcRequestTechnicalReviewPage;
